/**
 * MessageSkeletons component for displaying loading placeholders
 * Renders animated placeholders for message bubbles in chat interface
 */
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

/**
 * Animated pulse effect for skeleton components
 * Creates a shimmering loading animation
 */
const MessageSkeletonPulse = styled.div`
  display: inline-block;
  height: 100%;
  width: 100%;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.text.light + '11'} 25%,
    ${({ theme }) => theme.text.light + '22'} 37%,
    ${({ theme }) => theme.text.light + '11'} 63%
  );
  background-size: 400% 100%;
  animation: messagePulse 1.4s ease-in-out infinite;

  @keyframes messagePulse {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0 50%;
    }
  }
`;

/**
 * Message bubble skeleton with different shape based on sender type
 * Styled to match the shape of real message bubbles
 */
const SkeletonMessageBubble = styled.div<{ isUser?: boolean }>`
  height: 80px;
  width: ${({ theme }) => (theme.isMobile ? '73vw' : '60%')};
  border-radius: 20px 20px ${({ isUser }) => (isUser ? '3px' : '20px')} ${({ isUser }) => (isUser ? '20px' : '3px')};
  overflow: hidden;
`;

/**
 * Avatar placeholder for user/assistant icons
 * Circular shape to match avatar style
 */
const SkeletonAvatar = styled.div`
  width: ${({ theme }) => theme.text.s3};
  aspect-ratio: 1;
  border-radius: ${({ theme }) => `calc(${theme.text.s2} / 2)`};
  overflow: hidden;
`;

/**
 * Container for individual message skeleton items
 * Aligns messages based on sender type (user/assistant)
 */
const MessageSkeletonItem = styled(motion.div)<{ isUser: boolean }>`
  display: flex;
  gap: 1rem;
  padding: 2rem;
  align-items: flex-end;
  justify-content: ${({ isUser }) => (isUser ? 'flex-end' : 'flex-start')};
`;

/**
 * MessageSkeletons component that renders message loading placeholders
 * Creates alternating user and assistant message skeletons with staggered animation
 *
 * @returns Multiple pairs of animated message placeholders
 */
const MessageSkeletons = () => {
  return (
    <>
      {[...Array(3)].map((_, index) => (
        <React.Fragment key={index}>
          {/* Assistant Message Skeleton */}
          <MessageSkeletonItem
            isUser={false}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.15 }}
          >
            <SkeletonAvatar>
              <MessageSkeletonPulse />
            </SkeletonAvatar>
            <SkeletonMessageBubble isUser={false}>
              <MessageSkeletonPulse />
            </SkeletonMessageBubble>
          </MessageSkeletonItem>

          {/* User Message Skeleton */}
          <MessageSkeletonItem
            isUser={true}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.15 + 0.1 }}
          >
            <SkeletonMessageBubble isUser={true}>
              <MessageSkeletonPulse />
            </SkeletonMessageBubble>
            <SkeletonAvatar>
              <MessageSkeletonPulse />
            </SkeletonAvatar>
          </MessageSkeletonItem>
        </React.Fragment>
      ))}
    </>
  );
};

export default MessageSkeletons;
